import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Grid2 from '@mui/material/Unstable_Grid2/Grid2';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';
import { Navigate } from 'react-router-dom';
import { Cell, Legend, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';

import AuthContext from '../../context/authContext';
import useBranch from '../../hooks/useBranch';
import {
  getRechartsLegendPieProps,
  getRechartsTooltipProps,
  getStatisticsTotalsRowSx,
  viewRootTextPrimarySx,
} from '../../util/chartTheme';
import useStatisticCacheEntries from '../../hooks/useStatisticCacheEntries';
import useStatisticJob from '../../hooks/useStatisticJob';
import StatisticViewShell from '../ui/StatisticViewShell';
import { STATISTIC_TYPE_BON_ANALYSIS } from '../../constants/statisticTypes';

function formatEuroCents(cents) {
  const euros = Number(cents || 0) / 100;
  return (
    euros.toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €'
  );
}
function formatEuro(euros) {
  return (
    Number(euros || 0).toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' €'
  );
}
function formatPercent(fraction) {
  const val = Number(fraction || 0) * 100;
  return val.toLocaleString('de-DE', { minimumFractionDigits: 1, maximumFractionDigits: 1 }) + ' %';
}
function formatInt(value) {
  return Number(value || 0).toLocaleString('de-DE', { maximumFractionDigits: 0 });
}
function formatQty(value) {
  return Number(value || 0).toLocaleString('de-DE', { maximumFractionDigits: 2 });
}

// Count helper for the "aktuelle Daten" dropdown labels.
const countCsv = raw => String(raw || '').split(',').filter(Boolean).length;

const KPI = ({ label, value, sub }) => (
  <Paper
    elevation={0}
    sx={{ p: 2, border: '1px solid', borderColor: 'divider', textAlign: 'center', height: '100%' }}
  >
    <Typography variant='caption' color='text.secondary'>
      {label}
    </Typography>
    <Typography variant='h5' sx={{ mt: 0.5, fontWeight: 600 }}>
      {value}
    </Typography>
    {sub != null && (
      <Typography variant='caption' color='text.secondary' sx={{ mt: 0.5, display: 'block' }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

const SectionHeader = ({ children }) => (
  <Stack direction='row' alignItems='center' spacing={1} sx={{ mb: 1, mt: 1 }}>
    <Typography variant='h6'>{children}</Typography>
    <Divider sx={{ flex: 1 }} />
  </Stack>
);

const BonAnalysis = () => {
  const theme = useTheme();
  const totalsRowSx = useMemo(() => getStatisticsTotalsRowSx(theme), [theme]);
  const chartTooltipProps = useMemo(() => getRechartsTooltipProps(theme), [theme]);
  const chartLegendPieProps = useMemo(() => getRechartsLegendPieProps(theme), [theme]);
  const pieChartColors = useMemo(
    () => [
      theme.palette.primary.main,
      theme.palette.secondary.main,
      theme.palette.success.main,
      theme.palette.warning.main,
      theme.palette.error.main,
      theme.palette.info.main,
      theme.palette.primary.light,
      theme.palette.secondary.light,
      theme.palette.success.light,
      theme.palette.warning.light,
    ],
    [theme]
  );

  const authCtx = useContext(AuthContext);
  if (!authCtx.checkRight('statistic:read')) {
    return <Navigate to='/welcome' replace />;
  }

  const { getBranches, getAllEntertainmentsOfBranches } = useBranch();

  const [startDate, setStartDate] = useState(dayjs().startOf('day'));
  const [endDate, setEndDate] = useState(dayjs().endOf('day'));
  const [branchOptions, setBranchOptions] = useState([]);
  const [entertainmentOptions, setEntertainmentOptions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [entertainments, setEntertainments] = useState([]);
  const [includeDetails, setIncludeDetails] = useState(false);
  const [expandedBranches, setExpandedBranches] = useState({});
  const [tab, setTab] = useState(0);

  const [appliedFilter, setAppliedFilter] = useState({
    startDate: dayjs().startOf('day').format('YYYY-MM-DD'),
    endDate: dayjs().endOf('day').format('YYYY-MM-DD'),
    branchIds: [],
    entertainmentNames: [],
    includeDetails: false,
  });
  const [selectedEntryId, setSelectedEntryId] = useState(null);
  const [optionsLoading, setOptionsLoading] = useState(true);

  const toggleBranch = branchName => {
    setExpandedBranches(prev => ({ ...prev, [branchName]: !prev[branchName] }));
  };

  useEffect(() => {
    const f = async () => {
      const loaded = await getBranches();
      const opts = (loaded || []).map(b => ({ label: b.locationName, id: b._id }));
      setBranchOptions(opts);
      setBranches(opts);
      const allEnterts = await getAllEntertainmentsOfBranches((loaded || []).map(b => b._id));
      const entOpts = (allEnterts || []).map(e => ({
        label: typeof e === 'string' ? e : e.label || e.longName || '',
      }));
      setEntertainmentOptions(entOpts);
      setEntertainments(entOpts);
      setOptionsLoading(false);
    };
    f();
  }, []);

  useEffect(() => {
    const f = async () => {
      if (!branches.length) return;
      const branchIds = branches.map(b => b.id);
      const allEnterts = await getAllEntertainmentsOfBranches(branchIds);
      const entOpts = (allEnterts || []).map(e => ({
        label: typeof e === 'string' ? e : e.label || e.longName || '',
      }));
      setEntertainmentOptions(entOpts);
      setEntertainments(entOpts);
    };
    f();
  }, [branches]);

  const filterParams = useMemo(
    () => ({
      startDate: appliedFilter.startDate,
      endDate: appliedFilter.endDate,
      branches: appliedFilter.branchIds.join(','),
      entertainments: appliedFilter.entertainmentNames.join(','),
      includeDetails: appliedFilter.includeDetails ? '1' : '0',
      branchScope: 'all',
    }),
    [appliedFilter]
  );

  const {
    payload,
    isStale,
    cacheComputedAt,
    cacheExpiresAt,
    status,
    job,
    error,
    refresh,
    loadCacheEntry,
  } = useStatisticJob({
    statisticType: STATISTIC_TYPE_BON_ANALYSIS,
    endpoint: '/api/v1/bon/analysis',
    filterParams,
    autoFetchOnMount: true,
  });

  const { entries: cacheEntries, isLoading: entriesLoading } = useStatisticCacheEntries({
    statisticType: STATISTIC_TYPE_BON_ANALYSIS,
  });

  const handleApply = useCallback(
    (nextStart, nextEnd) => {
      const s = nextStart || startDate;
      const e = nextEnd || endDate;
      if (!s || !e || e.isBefore(s, 'day')) return;
      // "All selected" and "none selected" are semantically equivalent on the
      // backend ("no filter"). Normalize to an empty array so the cache key
      // stays stable and users don't accidentally spawn a second cache entry.
      const allBranchesSelected =
        branchOptions.length > 0 && branches.length === branchOptions.length;
      const allEntsSelected =
        entertainmentOptions.length > 0 && entertainments.length === entertainmentOptions.length;
      const branchIds = allBranchesSelected ? [] : [...branches.map(b => b.id)].sort();
      const entertainmentNames = allEntsSelected
        ? []
        : [...entertainments.map(en => en.label).filter(Boolean)].sort();
      setAppliedFilter({
        startDate: s.format('YYYY-MM-DD'),
        endDate: e.format('YYYY-MM-DD'),
        branchIds,
        entertainmentNames,
        includeDetails,
      });
      setSelectedEntryId(null);
    },
    [
      startDate,
      endDate,
      branches,
      branchOptions,
      entertainments,
      entertainmentOptions,
      includeDetails,
    ]
  );

  const handleSelectEntry = useCallback(
    entryId => {
      setSelectedEntryId(entryId || null);
      if (entryId) loadCacheEntry(entryId);
    },
    [loadCacheEntry]
  );

  const data = payload;
  const kpis = data?.kpis || null;

  const byBranchRows = useMemo(() => {
    if (!data) return [];
    const map = new Map();
    (data.revenueByBranchEntertainment || []).forEach(r => {
      const key = r.branchId || '—';
      if (!map.has(key)) map.set(key, { branchName: r.branchName || '—', total: 0, ents: {} });
      const row = map.get(key);
      row.total += r.revenueCents;
      const entName = r.entertainmentName || '—';
      row.ents[entName] = (row.ents[entName] || 0) + r.revenueCents;
    });
    const rows = Array.from(map.values()).map(row => {
      const entsArr = Object.entries(row.ents)
        .map(([name, revenueCents]) => ({ name, revenueCents }))
        .sort((a, b) => b.revenueCents - a.revenueCents);
      return { branchName: row.branchName, total: row.total, ents: entsArr };
    });
    return rows.sort((a, b) => b.total - a.total);
  }, [data]);

  const pieEntertainment = useMemo(() => {
    if (!data) return [];
    return (data.pieByEntertainment || [])
      .map(p => ({ name: p.entertainmentName || '—', value: (p.revenueCents || 0) / 100 }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const pieSuperGroup = useMemo(() => {
    if (!data) return [];
    return (data.pieBySuperGroup || [])
      .map(p => ({ name: p.superGroup || '—', value: (p.revenueCents || 0) / 100 }))
      .sort((a, b) => b.value - a.value);
  }, [data]);

  const grandTotalCents = useMemo(
    () => byBranchRows.reduce((sum, r) => sum + (r.total || 0), 0),
    [byBranchRows]
  );
  const totalEntertainmentEuros = useMemo(
    () => pieEntertainment.reduce((sum, r) => sum + (r.value || 0), 0),
    [pieEntertainment]
  );
  const totalSuperGroupEuros = useMemo(
    () => pieSuperGroup.reduce((sum, r) => sum + (r.value || 0), 0),
    [pieSuperGroup]
  );

  const groupRows = useMemo(() => {
    if (!data) return [];
    return [...(data.byGroup || [])].sort((a, b) => (b.revenueCents || 0) - (a.revenueCents || 0));
  }, [data]);
  const groupTotalCents = useMemo(
    () => groupRows.reduce((sum, r) => sum + (r.revenueCents || 0), 0),
    [groupRows]
  );

  const articleRows = useMemo(() => {
    if (!data) return [];
    return [...(data.byArticle || [])].sort((a, b) => (b.revenueCents || 0) - (a.revenueCents || 0));
  }, [data]);
  const articleTotalCents = useMemo(
    () => articleRows.reduce((sum, r) => sum + (r.revenueCents || 0), 0),
    [articleRows]
  );

  const reconciliationRows = data?.reconciliation?.rows || [];
  const reconciliationTotals = data?.reconciliation?.totals || {
    posCents: 0,
    onlineCents: 0,
    combinedCents: 0,
  };

  const dataQuality = data?.dataQuality || null;

  const payloadIncludeDetails = useMemo(() => {
    if (!data) return false;
    return Array.isArray(data.superGroups) && data.superGroups.length > 0;
  }, [data]);

  const applyIsDisabled =
    optionsLoading || !startDate || !endDate || endDate.isBefore(startDate, 'day');
  const refreshIsDisabled =
    status === 'fetching' || status === 'pending' || status === 'running';

  const shellFilterParamConfig = useMemo(
    () => ({
      branchScope: { hidden: true },
      branches: {
        label: 'Standorte',
        hidden: v => countCsv(v) === 0,
        format: v => String(countCsv(v)),
      },
      entertainments: {
        label: 'Entertainments',
        hidden: v => countCsv(v) === 0,
        format: v => String(countCsv(v)),
      },
      includeDetails: {
        label: 'Details',
        hidden: v => v !== '1',
        format: () => 'ja',
      },
    }),
    []
  );

  const singleLineAutocompleteSx = {
    '& .MuiAutocomplete-inputRoot': {
      flexWrap: 'nowrap',
      overflow: 'hidden',
    },
    '& .MuiAutocomplete-input': {
      minWidth: '30px !important',
      flex: '1 1 30px',
    },
  };

  const renderLimitedTags = max => (value, getTagProps) => {
    const displayed = value.slice(0, max);
    const remaining = value.length - displayed.length;
    return (
      <>
        {displayed.map((option, index) => {
          const { key, ...chipProps } = getTagProps({ index });
          return <Chip {...chipProps} key={key} label={(option && option.label) || ''} size='small' />;
        })}
        {remaining > 0 && (
          <Typography variant='body2' color='text.secondary' sx={{ ml: 0.5, alignSelf: 'center' }}>
            +{remaining}
          </Typography>
        )}
      </>
    );
  };

  const branchesFilterNode = (
    <Autocomplete
      multiple
      id='bon-analysis-branches'
      options={branchOptions}
      getOptionKey={option => option.id}
      getOptionLabel={option => (option && option.label) || ''}
      value={branches}
      onChange={(_e, val) => setBranches(val)}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      renderTags={renderLimitedTags(2)}
      sx={singleLineAutocompleteSx}
      renderInput={params => <TextField {...params} label='Standorte' variant='outlined' />}
    />
  );

  const entertainmentsFilterNode = (
    <Autocomplete
      multiple
      id='bon-analysis-entertainments'
      options={entertainmentOptions}
      getOptionLabel={option => (option && option.label) || ''}
      value={entertainments}
      onChange={(_e, val) => setEntertainments(val)}
      isOptionEqualToValue={(option, value) => option.label === value.label}
      renderTags={renderLimitedTags(2)}
      sx={singleLineAutocompleteSx}
      renderInput={params => <TextField {...params} label='Entertainments' variant='outlined' />}
    />
  );

  const togglesActionsNode = (
    <Stack direction='row' spacing={2} sx={{ flexWrap: 'wrap', rowGap: 0.5, alignItems: 'center' }}>
      <FormControlLabel
        control={
          <Switch
            checked={includeDetails}
            onChange={e => setIncludeDetails(e.target.checked)}
            size='small'
          />
        }
        label='Sammelwarengruppen-Details'
      />
    </Stack>
  );

  const renderRevenueTab = () => (
    <>
      <SectionHeader>Umsatz nach Standort und Entertainment</SectionHeader>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 60 }}></TableCell>
              <TableCell>Standort / Entertainment</TableCell>
              <TableCell align='right'>Umsatz</TableCell>
              {payloadIncludeDetails &&
                (data?.superGroups || []).map((sg, idx) => (
                  <TableCell key={idx} align='right'>
                    {sg.name || '—'}
                  </TableCell>
                ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {byBranchRows.map((row, idx) => (
              <React.Fragment key={idx}>
                <TableRow>
                  <TableCell>
                    <IconButton
                      aria-label='expand row'
                      size='small'
                      onClick={() => toggleBranch(row.branchName)}
                    >
                      {expandedBranches[row.branchName] ? (
                        <KeyboardArrowUpIcon />
                      ) : (
                        <KeyboardArrowDownIcon />
                      )}
                    </IconButton>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{row.branchName}</TableCell>
                  <TableCell align='right'>
                    {formatEuroCents(row.total)}
                    {grandTotalCents > 0 && (
                      <Typography
                        component='span'
                        variant='caption'
                        color='text.secondary'
                        sx={{ ml: 1 }}
                      >
                        {formatPercent(row.total / grandTotalCents)}
                      </Typography>
                    )}
                  </TableCell>
                  {payloadIncludeDetails &&
                    (data?.superGroups || []).map((sg, sIdx) => {
                      const val = (data?.byBranchSuperGroup || [])
                        .filter(
                          r =>
                            r.branchName === row.branchName &&
                            (r.superGroup || '—') === (sg.name || '—')
                        )
                        .reduce((sum, r) => sum + (r.revenueCents || 0), 0);
                      return (
                        <TableCell key={sIdx} align='right'>
                          {formatEuroCents(val)}
                        </TableCell>
                      );
                    })}
                </TableRow>

                {row.ents.map((e, eIdx) => (
                  <TableRow
                    key={eIdx}
                    style={{ display: expandedBranches[row.branchName] ? '' : 'none' }}
                  >
                    <TableCell></TableCell>
                    <TableCell sx={{ pl: 3 }}>{e.name}</TableCell>
                    <TableCell align='right'>
                      {formatEuroCents(e.revenueCents)}
                      {row.total > 0 && (
                        <Typography
                          component='span'
                          variant='caption'
                          color='text.secondary'
                          sx={{ ml: 1 }}
                        >
                          {formatPercent(e.revenueCents / row.total)}
                        </Typography>
                      )}
                    </TableCell>
                    {payloadIncludeDetails &&
                      (data?.superGroups || []).map((sg, sIdx) => {
                        const val = (data?.byBranchEntertainmentSuperGroup || [])
                          .filter(
                            r =>
                              r.branchName === row.branchName &&
                              r.entertainmentName === e.name &&
                              (r.superGroup || '—') === (sg.name || '—')
                          )
                          .reduce((sum, r) => sum + (r.revenueCents || 0), 0);
                        return (
                          <TableCell key={sIdx} align='right'>
                            {formatEuroCents(val)}
                          </TableCell>
                        );
                      })}
                  </TableRow>
                ))}
              </React.Fragment>
            ))}
            <TableRow sx={totalsRowSx}>
              <TableCell></TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Gesamt</TableCell>
              <TableCell align='right'>{formatEuroCents(grandTotalCents)}</TableCell>
              {payloadIncludeDetails &&
                (data?.superGroups || []).map((sg, sIdx) => {
                  const val = (data?.byBranchSuperGroup || [])
                    .filter(r => (r.superGroup || '—') === (sg.name || '—'))
                    .reduce((sum, r) => sum + (r.revenueCents || 0), 0);
                  return (
                    <TableCell key={sIdx} align='right'>
                      {formatEuroCents(val)}
                    </TableCell>
                  );
                })}
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>

      <Grid2 container spacing={2}>
        <Grid2 xs={12} md={6}>
          <Paper sx={{ p: 2 }} elevation={0}>
            <Typography variant='h6' sx={{ mb: 1 }}>
              Umsatz nach Entertainment
            </Typography>
            <Box sx={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieEntertainment}
                    dataKey='value'
                    nameKey='name'
                    outerRadius={120}
                    isAnimationActive={false}
                  >
                    {pieEntertainment.map((entry, index) => (
                      <Cell key={`cell-e-${index}`} fill={pieChartColors[index % pieChartColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    {...chartTooltipProps}
                    formatter={value =>
                      `${formatEuro(value)} (${formatPercent(value / (totalEntertainmentEuros || 1))})`
                    }
                  />
                  <Legend {...chartLegendPieProps} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid2>
        <Grid2 xs={12} md={6}>
          <Paper sx={{ p: 2 }} elevation={0}>
            <Typography variant='h6' sx={{ mb: 1 }}>
              Umsatz nach Sammelwarengruppe
            </Typography>
            <Box sx={{ width: '100%', height: 320 }}>
              <ResponsiveContainer>
                <PieChart>
                  <Pie
                    data={pieSuperGroup}
                    dataKey='value'
                    nameKey='name'
                    outerRadius={120}
                    isAnimationActive={false}
                  >
                    {pieSuperGroup.map((entry, index) => (
                      <Cell key={`cell-s-${index}`} fill={pieChartColors[index % pieChartColors.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    {...chartTooltipProps}
                    formatter={value =>
                      `${formatEuro(value)} (${formatPercent(value / (totalSuperGroupEuros || 1))})`
                    }
                  />
                  <Legend {...chartLegendPieProps} />
                </PieChart>
              </ResponsiveContainer>
            </Box>
          </Paper>
        </Grid2>
      </Grid2>
    </>
  );

  const renderProductsTab = () => (
    <>
      <SectionHeader>Umsatz nach Warengruppe</SectionHeader>
      <TableContainer component={Paper} sx={{ mb: 3 }}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Warengruppe</TableCell>
              <TableCell align='right'>Menge</TableCell>
              <TableCell align='right'>Umsatz</TableCell>
              <TableCell align='right'>Anteil</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {groupRows.map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>
                  {row.name || '—'}
                  {row.isGastro && (
                    <Chip label='Gastro' size='small' sx={{ ml: 1 }} variant='outlined' />
                  )}
                </TableCell>
                <TableCell align='right'>{formatQty(row.qty)}</TableCell>
                <TableCell align='right'>{formatEuroCents(row.revenueCents)}</TableCell>
                <TableCell align='right'>
                  <Typography variant='caption' color='text.secondary'>
                    {formatPercent((row.revenueCents || 0) / (groupTotalCents || 1))}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {groupRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={4}>
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant='body2' color='text.secondary'>
                      Keine Daten im gewählten Zeitraum.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {groupRows.length > 0 && (
              <TableRow sx={totalsRowSx}>
                <TableCell sx={{ fontWeight: 600 }}>Gesamt</TableCell>
                <TableCell></TableCell>
                <TableCell align='right'>{formatEuroCents(groupTotalCents)}</TableCell>
                <TableCell></TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <SectionHeader>Top-Artikel</SectionHeader>
      <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
        Die {articleRows.length} umsatzstärksten Artikel im gewählten Zeitraum.
      </Typography>
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Artikel</TableCell>
              <TableCell>Warengruppe</TableCell>
              <TableCell align='right'>Menge</TableCell>
              <TableCell align='right'>Umsatz</TableCell>
              <TableCell align='right'>Anteil</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {articleRows.map((row, idx) => (
              <TableRow key={row.articleCopagoId ?? idx}>
                <TableCell>{idx + 1}</TableCell>
                <TableCell>{row.name || '—'}</TableCell>
                <TableCell>{row.groupName || '—'}</TableCell>
                <TableCell align='right'>{formatQty(row.qty)}</TableCell>
                <TableCell align='right'>{formatEuroCents(row.revenueCents)}</TableCell>
                <TableCell align='right'>
                  <Typography variant='caption' color='text.secondary'>
                    {formatPercent((row.revenueCents || 0) / (articleTotalCents || 1))}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {articleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6}>
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant='body2' color='text.secondary'>
                      Keine Daten im gewählten Zeitraum.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const renderReconciliationTab = () => (
    <>
      <SectionHeader>Abgleich Online ↔ POS (Copago)</SectionHeader>
      <Typography variant='caption' color='text.secondary' sx={{ mb: 1, display: 'block' }}>
        Gegenüberstellung des an der Kasse erfassten Copago-Umsatzes (POS) und des online
        gebuchten Umsatzes (Regiondo) je Standort.
      </Typography>
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Standort</TableCell>
              <TableCell align='right'>POS (Copago)</TableCell>
              <TableCell align='right'>Online (Regiondo)</TableCell>
              <TableCell align='right'>Gesamt</TableCell>
              <TableCell align='right'>Online-Anteil</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reconciliationRows.map((row, idx) => (
              <TableRow key={row.branchId ?? idx}>
                <TableCell>{row.branchName || '—'}</TableCell>
                <TableCell align='right'>{formatEuroCents(row.posCents)}</TableCell>
                <TableCell align='right'>{formatEuroCents(row.onlineCents)}</TableCell>
                <TableCell align='right'>{formatEuroCents(row.combinedCents)}</TableCell>
                <TableCell align='right'>
                  <Typography variant='caption' color='text.secondary'>
                    {formatPercent((row.onlineSharePct || 0) / 100)}
                  </Typography>
                </TableCell>
              </TableRow>
            ))}
            {reconciliationRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant='body2' color='text.secondary'>
                      Keine Daten im gewählten Zeitraum.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {reconciliationRows.length > 0 && (
              <TableRow sx={totalsRowSx}>
                <TableCell sx={{ fontWeight: 600 }}>Gesamt</TableCell>
                <TableCell align='right'>{formatEuroCents(reconciliationTotals.posCents)}</TableCell>
                <TableCell align='right'>
                  {formatEuroCents(reconciliationTotals.onlineCents)}
                </TableCell>
                <TableCell align='right'>
                  {formatEuroCents(reconciliationTotals.combinedCents)}
                </TableCell>
                <TableCell align='right'>
                  {formatPercent(
                    reconciliationTotals.combinedCents
                      ? reconciliationTotals.onlineCents / reconciliationTotals.combinedCents
                      : 0
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const renderDataQualityTab = () => (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPI label='Anzahl Bons gesamt' value={formatInt(dataQuality?.totalBons)} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI
            label='Bons ohne Standort'
            value={`${formatInt(dataQuality?.withoutBranch)} (${formatQty(
              dataQuality?.withoutBranchPct
            )}%)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI
            label='Bons ohne Entertainment'
            value={`${formatInt(dataQuality?.withoutEntertainment)} (${formatQty(
              dataQuality?.withoutEntertainmentPct
            )}%)`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI
            label='Positionen ohne Warengruppe'
            value={formatInt(dataQuality?.positionsWithoutGroup)}
            sub={`von ${formatInt(dataQuality?.positionCount)} Positionen`}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPI
            label='Positionen ohne Artikel-Match'
            value={formatInt(dataQuality?.positionsWithoutArticle)}
            sub={`von ${formatInt(dataQuality?.positionCount)} Positionen`}
          />
        </Grid>
      </Grid>

      <SectionHeader>Aufschlüsselung nach Standort</SectionHeader>
      <TableContainer component={Paper}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              <TableCell>Standort</TableCell>
              <TableCell align='right'>Bons</TableCell>
              <TableCell align='right'>Ohne Entertainment</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(dataQuality?.byBranch || []).map((row, idx) => (
              <TableRow key={idx}>
                <TableCell>{row.branchName || '—'}</TableCell>
                <TableCell align='right'>{formatInt(row.bons)}</TableCell>
                <TableCell align='right'>{formatInt(row.withoutEntertainment)}</TableCell>
              </TableRow>
            ))}
            {(dataQuality?.byBranch || []).length === 0 && (
              <TableRow>
                <TableCell colSpan={3}>
                  <Box sx={{ py: 2, textAlign: 'center' }}>
                    <Typography variant='body2' color='text.secondary'>
                      Keine Daten im gewählten Zeitraum.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );

  const renderOverviewTab = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={6} md={3}>
        <KPI label='Umsatz gesamt' value={formatEuroCents(kpis?.totalRevenueCents)} />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPI label='Anzahl Bons' value={formatInt(kpis?.totalBons)} />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPI label='Ø Bon' value={formatEuroCents(kpis?.avgBasketCents)} />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPI label='Verkaufte Artikel' value={formatQty(kpis?.itemsSold)} />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPI
          label='Eintritt / Spiel'
          value={formatEuroCents(kpis?.admissionRevenueCents)}
          sub='Nicht-Gastro-Warengruppen'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPI
          label='Gastronomie'
          value={formatEuroCents(kpis?.gastroRevenueCents)}
          sub='Gastro-Warengruppen'
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPI
          label='In-Haus / Außer-Haus'
          value={formatEuroCents(kpis?.inHouseRevenueCents)}
          sub={`Außer-Haus: ${formatEuroCents(kpis?.takeawayRevenueCents)}`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPI
          label='MwSt / Rabatte'
          value={formatEuroCents(kpis?.vatCents)}
          sub={`Rabatte: ${formatEuroCents(kpis?.discountCents)}`}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPI label='Stornos' value={formatInt(kpis?.cancellationBons)} sub='Storno-Bons' />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <KPI
          label='Online-Umsatz (Regiondo)'
          value={formatEuroCents(reconciliationTotals.onlineCents)}
          sub={`Gesamt inkl. Online: ${formatEuroCents(reconciliationTotals.combinedCents)}`}
        />
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ ...viewRootTextPrimarySx }}>
      <StatisticViewShell
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        extraFilters={[branchesFilterNode, entertainmentsFilterNode]}
        onApply={handleApply}
        applyDisabled={applyIsDisabled}
        applyLabel='Auswerten'
        applyExtraActions={togglesActionsNode}
        status={status}
        isStale={isStale}
        cacheComputedAt={cacheComputedAt}
        cacheExpiresAt={cacheExpiresAt}
        job={job}
        error={error}
        cacheEntries={cacheEntries}
        selectedEntryId={selectedEntryId}
        onSelectEntry={handleSelectEntry}
        entriesLoading={entriesLoading}
        onRefresh={refresh}
        refreshDisabled={refreshIsDisabled}
        filterParamConfig={shellFilterParamConfig}
        hasPayload={payload != null}
        externallyLoading={false}
      >
        {data && (
          <>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={tab} onChange={(_e, v) => setTab(v)} variant='scrollable' scrollButtons='auto'>
                <Tab label='Übersicht' />
                <Tab label='Umsatz' />
                <Tab label='Produkte' />
                <Tab label='Abgleich' />
                <Tab label='Datenqualität' />
              </Tabs>
            </Box>
            {tab === 0 && renderOverviewTab()}
            {tab === 1 && renderRevenueTab()}
            {tab === 2 && renderProductsTab()}
            {tab === 3 && renderReconciliationTab()}
            {tab === 4 && renderDataQualityTab()}
          </>
        )}
      </StatisticViewShell>
    </Box>
  );
};

export default BonAnalysis;
